import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  InputAdornment,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Castle,
  Add,
  Edit,
  PersonAdd,
  MoreVert,
  Search,
  TrendingUp,
  CloudUpload,
  Image,
  ExitToApp
} from '@mui/icons-material';
import CharacterAvatar from './CharacterAvatar';
import TreasuryManagement from './TreasuryManagement';
import EntityCreationWarning from './EntityCreationWarning';
import { getApiUrl } from '../config';
import { useUGC } from '../contexts/UGCContext';
import {
  GlassBackground,
  GlassContainer,
  GlassCard,
  GlassButton,
  GlassIconButton,
  GlassChip,
  GlassShimmer,
  GlassDialog,
  GlassTextField,
  useGlassShimmer,
  GlassInfoCard,
} from './ui/GlassComponents';
import {
  GlassPageHeader,
} from './ui/GlassLayoutComponents';

const NationManagement = ({ sessionData, onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const shimmerRef = useGlassShimmer('.glass-card, .management-card');

  const [nations, setNations] = useState([]);
  const [selectedNation, setSelectedNation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [leavingNation, setLeavingNation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // UGC context
  const { getImageUrl } = useUGC();
  
  // Nation icon upload state
  const [iconUploadOpen, setIconUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Form states
  const [newNation, setNewNation] = useState({
    name: '',
    abbreviation: '',
    description: ''
  });
  const [editNation, setEditNation] = useState({
    name: '',
    abbreviation: '',
    description: ''
  });
  const [invitePlayer, setInvitePlayer] = useState('');

  const apiCall = useCallback(async (endpoint, options = {}) => {
    try {
      const response = await fetch(getApiUrl(endpoint), {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
          ...options.headers
        }
      });

      if (!response.ok) {
        // Try to get error message from response body
        try {
          const errorData = await response.json();
          if (errorData.message) {
            throw new Error(errorData.message);
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        } catch (parseError) {
          // If we can't parse the response, check if it's a known error status
          if (response.status === 404) {
            throw new Error('Player not found');
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }
      }

      return await response.json();
    } catch (err) {
      console.error('API call failed:', err);
      throw err;
    }
  }, [sessionData.token]);

  const fetchNations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall('/nations');
      setNations(data.nations || []);
    } catch (err) {
      setError('Failed to fetch nations');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const fetchNationDetails = async (nationId) => {
    try {
      const data = await apiCall(`/nations/${nationId}`);
      setSelectedNation(data);
    } catch (err) {
      setError('Failed to fetch nation details');
    }
  };

  const createNation = async () => {
    try {
      const data = await apiCall('/nations', {
        method: 'POST',
        body: JSON.stringify({
          ...newNation,
          founderId: sessionData.selectedDisplayName
        })
      });

      if (data.success) {
        setSuccess('Nation created successfully!');
        setCreateDialogOpen(false);
        setNewNation({ name: '', abbreviation: '', description: '' });
        fetchNations();
      }
    } catch (err) {
      setError('Failed to create nation');
    }
  };

  const updateNation = async () => {
    try {
      const data = await apiCall(`/nations/${selectedNation.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...editNation,
          editedBy: sessionData.selectedDisplayName
        })
      });

      if (data.success) {
        setSuccess('Nation updated successfully!');
        setEditDialogOpen(false);
        fetchNationDetails(selectedNation.id);
        fetchNations();
      }
    } catch (err) {
      setError('Failed to update nation');
    }
  };

  const inviteMember = async () => {
    try {
      const data = await apiCall(`/nations/${selectedNation.id}/members`, {
        method: 'POST',
        body: JSON.stringify({
          playerId: invitePlayer,
          invitedBy: sessionData.selectedDisplayName
        })
      });

      if (data.success) {
        setSuccess('Member invited successfully!');
        setInviteDialogOpen(false);
        setInvitePlayer('');
        fetchNationDetails(selectedNation.id);
      }
    } catch (err) {
      if (err.message.includes('Player not found')) {
        setError(`Player "${invitePlayer}" does not exist. Please check the spelling.`);
      } else {
        setError(err.message || 'Failed to invite member');
      }
    }
  };

  const promoteMember = async (playerId) => {
    try {
      const data = await apiCall(`/nations/${selectedNation.id}/members/${playerId}/promote`, {
        method: 'PUT',
        body: JSON.stringify({
          promotedBy: sessionData.selectedDisplayName
        })
      });

      if (data.success) {
        setSuccess('Member promoted successfully!');
        fetchNationDetails(selectedNation.id);
      }
    } catch (err) {
      setError('Failed to promote member');
    }
  };

  const demoteMember = async (playerId) => {
    try {
      const data = await apiCall(`/nations/${selectedNation.id}/members/${playerId}/demote`, {
        method: 'PUT',
        body: JSON.stringify({
          demotedBy: sessionData.selectedDisplayName
        })
      });

      if (data.success) {
        setSuccess('Member demoted successfully!');
        fetchNationDetails(selectedNation.id);
      }
    } catch (err) {
      setError('Failed to demote member');
    }
  };

  const removeMember = async (playerId) => {
    try {
      const data = await apiCall(`/nations/${selectedNation.id}/members/${playerId}`, {
        method: 'DELETE'
      });

      if (data.success) {
        setSuccess('Member removed successfully!');
        fetchNationDetails(selectedNation.id);
      }
    } catch (err) {
      setError('Failed to remove member');
    }
  };

  const leaveNation = async () => {
    setLeavingNation(true);
    try {
      const data = await apiCall(`/nations/${selectedNation.id}/members/${sessionData.selectedDisplayName}`, {
        method: 'DELETE'
      });

      if (data.success) {
        setSuccess('You have left the nation successfully!');
        // Go back to nations list since we're no longer a member
        setTabValue(0);
        setSelectedNation(null);
        fetchNations();
      }
    } catch (err) {
      setError('Failed to leave nation');
    } finally {
      setLeavingNation(false);
    }
  };

  useEffect(() => {
    fetchNations();
  }, [fetchNations]);


  const handleNationClick = (nation) => {
    setSelectedNation(nation);
    fetchNationDetails(nation.id);
    setTabValue(1);
  };

  const handleCreateClick = () => {
    setNewNation({ name: '', abbreviation: '', description: '' });
    setCreateDialogOpen(true);
  };

  const handleEditClick = () => {
    setEditNation({
      name: selectedNation.name,
      abbreviation: selectedNation.abbreviation,
      description: selectedNation.description
    });
    setEditDialogOpen(true);
  };

  const handleMemberMenuClick = (event, member) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMemberMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMember(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleDateString();
  };

  const isUserOfficer = (nation) => {
    return nation?.officers?.some(officer => officer.name === sessionData.selectedDisplayName);
  };

  const isUserMember = (nation) => {
    if (!nation) return false;
    return nation.officers?.some(officer => officer.name === sessionData.selectedDisplayName) ||
           nation.citizens?.some(citizen => citizen.name === sessionData.selectedDisplayName);
  };

  const handleIconUpload = () => {
    setIconUploadOpen(true);
    setUploadError('');
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setUploadError('Please select a PNG, JPEG, or WebP image file.');
        return;
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setUploadError('File size must be less than 5MB.');
        return;
      }

      setSelectedFile(file);
      setUploadError('');
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file');
      return;
    }

    setUploadLoading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch(getApiUrl(`/nations/${selectedNation.id}/icon/upload`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionData.token}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIconUploadOpen(false);
        setSelectedFile(null);
        setPreviewUrl('');
        
        // Refresh nation details to show new icon
        await fetchNationDetails(selectedNation.id);
        setSuccess('Nation icon uploaded successfully!');
      } else {
        setUploadError(result.message || 'Failed to upload nation icon');
      }
    } catch (err) {
      setUploadError('Failed to connect to server');
    } finally {
      setUploadLoading(false);
    }
  };

  const resetIconUpload = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setUploadError('');
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  const renderNationsList = () => {
    // Filter nations based on search term
    const filteredNations = nations.filter(nation =>
      nation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nation.abbreviation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (nation.description && nation.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Sort nations by activity (most active first)
    const sortedNations = [...filteredNations].sort((a, b) => {
      // First sort by activity status (active nations first)
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      
      // Then sort by activity percentage (higher percentage first)
      if (a.activityPercentage !== b.activityPercentage) {
        return b.activityPercentage - a.activityPercentage;
      }
      
      // Finally sort by total members (larger nations first)
      return b.totalMembers - a.totalMembers;
    });

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
          <GlassButton
            variant="glass"
            size="small"
            onClick={handleCreateClick}
          >
            <Add sx={{ fontSize: '1rem', mr: 0.5 }} />
            {!isMobile && 'Create Nation'}
          </GlassButton>
        </Box>

        {/* Search Field */}
        <GlassTextField
          fullWidth
          variant="outlined"
          placeholder="Search nations by name, abbreviation, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(255, 255, 255, 0.5)',
              opacity: 1,
            },
          }}
        />

        {loading ? (
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>Loading nations...</Typography>
        ) : sortedNations.length === 0 ? (
          searchTerm ? (
            <GlassInfoCard severity="info">No nations found matching "{searchTerm}". Try a different search term.</GlassInfoCard>
          ) : (
            <GlassInfoCard severity="info">No nations found. Create the first nation!</GlassInfoCard>
          )
        ) : (
          <Grid container spacing={isMobile ? 2 : 3}>
            {sortedNations.map((nation) => (
              <Grid item xs={12} md={6} lg={4} key={nation.id}>
                <GlassCard
                  className="management-card"
                  clickable
                  onClick={() => handleNationClick(nation)}
                >
                  <Box sx={{ p: isMobile ? 2 : 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        src={getImageUrl(nation.profilePictureUrl)}
                        sx={{ bgcolor: 'rgba(102, 126, 234, 0.9)', mr: 2 }}
                      >
                        <Castle />
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>{nation.name}</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {nation.abbreviation}
                        </Typography>
                      </Box>
                      {nation.isActive && (
                        <Tooltip title={`Active nation: ${Math.round(nation.activityPercentage * 100)}% of members have been online in the past 2 weeks`}>
                          <GlassChip
                            variant="success"
                            label="ACTIVE"
                            size="small"
                            sx={{ '& .MuiChip-icon': { color: 'inherit' } }}
                          />
                        </Tooltip>
                      )}
                    </Box>

                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                      {nation.description || 'No description provided'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      <GlassChip
                        label={`${nation.officerCount} Officers`}
                        size="small"
                        sx={{ '& .MuiChip-label': { display: 'flex', alignItems: 'center', gap: 0.5 } }}
                      />
                      <GlassChip
                        label={`${nation.citizenCount} Citizens`}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        Created: {formatDate(nation.creationTime)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        {nation.activeMembersCount}/{nation.totalMembers} active
                      </Typography>
                    </Box>
                  </Box>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  const renderTabNavigation = () => {
    if (!selectedNation || !isUserOfficer(selectedNation)) return null;

    const userIsMember = isUserMember(selectedNation);

    return (
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 3,
        flexWrap: 'wrap',
        justifyContent: isMobile ? 'center' : 'space-between',
        alignItems: 'center'
      }}>
        {/* Left side - Tab Navigation for Officers */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            width: isMobile ? '100%' : '400px',
            height: isMobile ? '48px' : '56px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px)',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
          }}
        >
          {/* Sliding background indicator */}
          <Box
            sx={{
              position: 'absolute',
              top: '4px',
              left: tabValue === 1 ? '4px' : 'calc(50% + 2px)',
              height: 'calc(100% - 8px)',
              width: 'calc(50% - 6px)',
              borderRadius: '4px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(20px)',
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 1,
            }}
          />

          <Box
            onClick={() => setTabValue(1)}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '50%',
              height: '100%',
              cursor: 'pointer',
              zIndex: 2,
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              fontWeight: 500,
              transition: 'color 0.3s ease',
              color: tabValue === 1 ? 'white' : 'rgba(255, 255, 255, 0.6)',
            }}
          >
            Nation Details
          </Box>
          <Box
            onClick={() => setTabValue(2)}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              width: '50%',
              height: '100%',
              cursor: 'pointer',
              zIndex: 2,
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              fontWeight: 500,
              transition: 'color 0.3s ease',
              color: tabValue === 2 ? 'white' : 'rgba(255, 255, 255, 0.6)',
            }}
          >
            {!isMobile && <TrendingUp sx={{ fontSize: '1.1rem' }} />}
            Treasury
          </Box>
        </Box>

        {/* Right side - Action Buttons */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          ml: isMobile ? 0 : 'auto',
          justifyContent: isMobile ? 'center' : 'flex-start',
          width: isMobile ? '100%' : 'auto'
        }}>
          {userIsMember && (
            <GlassButton
              variant="glass"
              onClick={leaveNation}
              disabled={leavingNation}
              sx={{
                color: 'rgba(255, 107, 107, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                flex: isMobile ? '1' : '0 1 auto',
                minWidth: isMobile ? 0 : 'auto'
              }}
            >
              <ExitToApp sx={{ fontSize: '1.2rem' }} />
              {leavingNation ? 'Leaving...' : (!isMobile && 'Leave Nation')}
            </GlassButton>
          )}
          {isUserOfficer(selectedNation) && (
            <>
              <GlassButton
                variant="glass"
                onClick={handleEditClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  flex: isMobile ? '1' : '0 1 auto',
                  minWidth: isMobile ? 0 : 'auto'
                }}
              >
                <Edit sx={{ fontSize: '1.2rem' }} />
                {!isMobile && 'Edit'}
              </GlassButton>
              <GlassButton
                variant="glass"
                onClick={() => setInviteDialogOpen(true)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  flex: isMobile ? '1' : '0 1 auto',
                  minWidth: isMobile ? 0 : 'auto'
                }}
              >
                <PersonAdd sx={{ fontSize: '1.2rem' }} />
                {!isMobile && 'Invite Member'}
              </GlassButton>
            </>
          )}
        </Box>
      </Box>
    );
  };

  const renderNationDetails = () => {
    if (!selectedNation) return null;

    const userIsOfficer = isUserOfficer(selectedNation);

    return (
      <Box>

        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12} md={8}>
            <GlassCard sx={{ mb: 3, height: 'auto' }}>
              <Box sx={{ p: isMobile ? 2 : 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
                  Description
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {selectedNation.description || 'No description provided'}
                </Typography>
              </Box>
            </GlassCard>

            <GlassCard sx={{ height: 'auto', minHeight: 'auto', overflow: 'visible' }}>
              <Box sx={{ p: isMobile ? 2 : 3, overflow: 'visible' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
                  Officers ({selectedNation.officers?.length || 0})
                </Typography>
              <List sx={{ width: '100%', py: 0 }}>
                {selectedNation.officers?.map((officer) => (
                  <ListItem
                    key={officer.id}
                    secondaryAction={
                      userIsOfficer && officer.name !== sessionData.selectedDisplayName && (
                        <IconButton
                          edge="end"
                          onClick={(e) => handleMemberMenuClick(e, { ...officer, role: 'officer' })}
                        >
                          <MoreVert />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemAvatar>
                      <CharacterAvatar
                        profilePictureUrl={officer.profilePictureUrl}
                        characterName={officer.name}
                        isDeceased={Boolean(officer.isDeceased)}
                        size={40}
                        sx={{ bgcolor: 'primary.main' }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span style={{ color: 'white' }}>{officer.name}</span>
                          {Boolean(officer.isDeceased) && (
                            <GlassChip
                              variant="deceased"
                              label="Deceased"
                              size="small"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Officer</Typography>
                          {officer.accountOwner && (
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', ml: 1 }}>
                              ({officer.accountOwner})
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
                Citizens ({selectedNation.citizens?.filter(citizen =>
                  !selectedNation.officers?.some(officer => officer.id === citizen.id)
                ).length || 0})
              </Typography>
              <List>
                {selectedNation.citizens?.filter(citizen => 
                  !selectedNation.officers?.some(officer => officer.id === citizen.id)
                ).map((citizen) => (
                  <ListItem
                    key={citizen.id}
                    secondaryAction={
                      userIsOfficer && (
                        <IconButton
                          edge="end"
                          onClick={(e) => handleMemberMenuClick(e, { ...citizen, role: 'citizen' })}
                        >
                          <MoreVert />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemAvatar>
                      <CharacterAvatar
                        profilePictureUrl={citizen.profilePictureUrl}
                        characterName={citizen.name}
                        isDeceased={Boolean(citizen.isDeceased)}
                        size={40}
                        sx={{ bgcolor: 'secondary.main' }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span style={{ color: 'white' }}>{citizen.name}</span>
                          {Boolean(citizen.isDeceased) && (
                            <GlassChip
                              variant="deceased"
                              label="Deceased"
                              size="small"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Citizen</Typography>
                          {citizen.accountOwner && (
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', ml: 1 }}>
                              ({citizen.accountOwner})
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              </Box>
            </GlassCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <GlassCard sx={{ height: 'auto' }}>
              <Box sx={{ p: isMobile ? 2 : 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
                  Nation Info
                </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  <strong>Full Name:</strong> {selectedNation.name}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  <strong>Abbreviation:</strong> {selectedNation.abbreviation}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  <strong>Created:</strong> {formatDate(selectedNation.creationTime)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  <strong>National Currency:</strong> {selectedNation.nationalCurrency || 'None'}
                </Typography>
              </Box>

              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <GlassChip
                  label={`${selectedNation.officers?.length || 0} Officers`}
                  size="small"
                />
                <GlassChip
                  label={`${selectedNation.citizens?.filter(citizen =>
                    !selectedNation.officers?.some(officer => officer.id === citizen.id)
                  ).length || 0} Citizens`}
                  size="small"
                />
              </Box>
              </Box>
            </GlassCard>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <GlassBackground>
      <GlassShimmer ref={shimmerRef} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
        <GlassContainer className="glass-container">
          {tabValue === 0 ? (
            // Nations List View
            <>
              <GlassPageHeader
                title="Nation Management"
                subtitle={`Managing nations for ${sessionData.selectedDisplayName}`}
                action={
                  <GlassIconButton onClick={onBack}>
                    <ArrowBack />
                  </GlassIconButton>
                }
              />

              {error && (
                <GlassInfoCard severity="error" onClose={() => setError('')}>
                  {error}
                </GlassInfoCard>
              )}

              {success && (
                <GlassInfoCard severity="success" onClose={() => setSuccess('')}>
                  {success}
                </GlassInfoCard>
              )}

              {renderNationsList()}
            </>
          ) : (
            // Nation Details/Treasury View
            <>
              <GlassPageHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={getImageUrl(selectedNation?.profilePictureUrl)}
                      onClick={isUserOfficer(selectedNation) ? handleIconUpload : undefined}
                      sx={{
                        bgcolor: 'rgba(102, 126, 234, 0.9)',
                        width: isMobile ? 48 : 56,
                        height: isMobile ? 48 : 56,
                        cursor: isUserOfficer(selectedNation) ? 'pointer' : 'default',
                        transition: 'transform 0.2s ease',
                        '&:hover': isUserOfficer(selectedNation) ? {
                          transform: 'scale(1.05)',
                        } : {}
                      }}
                    >
                      <Castle />
                    </Avatar>
                    <Box>
                      <Typography
                        variant={isMobile ? "h5" : "h4"}
                        component="div"
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: isMobile ? '1.8rem' : '2.5rem',
                        }}
                      >
                        {selectedNation?.name}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: isMobile ? '0.9rem' : '1.1rem'
                        }}
                      >
                        {selectedNation?.abbreviation}
                      </Typography>
                    </Box>
                  </Box>
                }
                action={
                  <GlassIconButton
                    onClick={() => {
                      setTabValue(0);
                      setSelectedNation(null);
                    }}
                  >
                    <ArrowBack />
                  </GlassIconButton>
                }
              />

              {error && (
                <GlassInfoCard severity="error" onClose={() => setError('')}>
                  {error}
                </GlassInfoCard>
              )}

              {success && (
                <GlassInfoCard severity="success" onClose={() => setSuccess('')}>
                  {success}
                </GlassInfoCard>
              )}

              {renderTabNavigation()}

              {tabValue === 1 && renderNationDetails()}
              {tabValue === 2 && selectedNation && isUserOfficer(selectedNation) && (
                <TreasuryManagement
                  nationName={selectedNation.name}
                  sessionData={sessionData}
                />
              )}
            </>
          )}
        </GlassContainer>
      </Container>

      {/* Create Nation Dialog */}
      <GlassDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        title="Create New Nation"
        maxWidth="md"
        actions={
          <>
            <GlassButton variant="glass" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </GlassButton>
            <GlassButton
              variant="glass"
              onClick={createNation}
              disabled={!newNation.name || !newNation.abbreviation || newNation.abbreviation.length !== 2}
            >
              Create Nation
            </GlassButton>
          </>
        }
      >
        <EntityCreationWarning
          entityType="nation"
          additionalWarning="Is this a nation or a small town? Small towns may become nations, but should consider joining an existing nation for protection and resources."
        />
        <GlassTextField
          autoFocus
          margin="normal"
          label="Nation Name"
          fullWidth
          variant="outlined"
          value={newNation.name}
          onChange={(e) => setNewNation({ ...newNation, name: e.target.value })}
        />
        <GlassTextField
          margin="normal"
          label="Abbreviation (exactly 2 characters)"
          fullWidth
          variant="outlined"
          value={newNation.abbreviation}
          onChange={(e) => setNewNation({ ...newNation, abbreviation: e.target.value.toUpperCase() })}
          inputProps={{ maxLength: 2 }}
          error={newNation.abbreviation.length > 0 && newNation.abbreviation.length !== 2}
          helperText={newNation.abbreviation.length > 0 && newNation.abbreviation.length !== 2 ? "Must be exactly 2 characters" : ""}
        />
        <GlassTextField
          margin="normal"
          label="Description"
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          value={newNation.description}
          onChange={(e) => setNewNation({ ...newNation, description: e.target.value })}
        />
      </GlassDialog>

      {/* Edit Nation Dialog */}
      <GlassDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="Edit Nation"
        actions={
          <>
            <GlassButton variant="glass" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </GlassButton>
            <GlassButton
              variant="glass"
              onClick={updateNation}
              disabled={!editNation.name || !editNation.abbreviation || editNation.abbreviation.length !== 2}
            >
              Update Nation
            </GlassButton>
          </>
        }
      >
        <GlassTextField
          autoFocus
          margin="normal"
          label="Nation Name"
          fullWidth
          variant="outlined"
          value={editNation.name}
          onChange={(e) => setEditNation({ ...editNation, name: e.target.value })}
        />
        <GlassTextField
          margin="normal"
          label="Abbreviation (exactly 2 characters)"
          fullWidth
          variant="outlined"
          value={editNation.abbreviation}
          onChange={(e) => setEditNation({ ...editNation, abbreviation: e.target.value.toUpperCase() })}
          inputProps={{ maxLength: 2 }}
          error={editNation.abbreviation.length > 0 && editNation.abbreviation.length !== 2}
          helperText={editNation.abbreviation.length > 0 && editNation.abbreviation.length !== 2 ? "Must be exactly 2 characters" : ""}
        />
        <GlassTextField
          margin="normal"
          label="Description"
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          value={editNation.description}
          onChange={(e) => setEditNation({ ...editNation, description: e.target.value })}
        />
      </GlassDialog>

      {/* Invite Member Dialog */}
      <GlassDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        title="Invite Member"
        actions={
          <>
            <GlassButton variant="glass" onClick={() => setInviteDialogOpen(false)}>
              Cancel
            </GlassButton>
            <GlassButton
              variant="glass"
              onClick={inviteMember}
              disabled={!invitePlayer}
            >
              Invite Player
            </GlassButton>
          </>
        }
      >
        <GlassTextField
          autoFocus
          margin="normal"
          label="Player Name"
          fullWidth
          variant="outlined"
          value={invitePlayer}
          onChange={(e) => setInvitePlayer(e.target.value)}
          helperText="Enter the player's display name"
        />
      </GlassDialog>

      {/* Member Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMemberMenuClose}
      >
        {selectedMember && [
          selectedMember.role === 'citizen' && (
            <MenuItem key="promote" onClick={() => {
              promoteMember(selectedMember.id || selectedMember.name);
              handleMemberMenuClose();
            }}>
              Promote to Officer
            </MenuItem>
          ),
          selectedMember.role === 'officer' && (
            <MenuItem key="demote" onClick={() => {
              demoteMember(selectedMember.id || selectedMember.name);
              handleMemberMenuClose();
            }}>
              Demote to Citizen
            </MenuItem>
          ),
          <MenuItem key="remove" onClick={() => {
            removeMember(selectedMember.id || selectedMember.name);
            handleMemberMenuClose();
          }}>
            Remove from Nation
          </MenuItem>
        ].filter(Boolean)}
      </Menu>

      {/* Nation Icon Upload Dialog */}
      <GlassDialog
        open={iconUploadOpen}
        onClose={() => {
          setIconUploadOpen(false);
          resetIconUpload();
        }}
        title="Upload Nation Icon"
        actions={
          <>
            <GlassButton
              variant="glass"
              onClick={() => {
                setIconUploadOpen(false);
                resetIconUpload();
              }}
              disabled={uploadLoading}
            >
              Cancel
            </GlassButton>
            <GlassButton
              variant="glass"
              onClick={handleUploadSubmit}
              disabled={!selectedFile || uploadLoading}
            >
              {uploadLoading ? 'Uploading...' : <><CloudUpload sx={{ mr: 0.5, fontSize: '1.1rem' }} /> Upload Icon</>}
            </GlassButton>
          </>
        }
      >
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
          Upload an icon for {selectedNation?.name}. Supported formats: PNG, JPEG, WebP. Maximum file size: 5MB.
        </Typography>

        {selectedNation?.profilePictureUrl && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}>Current Icon:</Typography>
            <Avatar
              src={getImageUrl(selectedNation.profilePictureUrl)}
              alt={selectedNation.name}
              sx={{ width: 100, height: 100, mx: 'auto' }}
            >
              <Castle />
            </Avatar>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <input
            accept="image/png,image/jpeg,image/jpg,image/webp"
            style={{ display: 'none' }}
            id="icon-upload-input"
            type="file"
            onChange={handleFileSelect}
          />
          <label htmlFor="icon-upload-input">
            <GlassButton
              variant="glass"
              component="span"
              sx={{ width: '100%', mb: 2 }}
            >
              <Image sx={{ mr: 1 }} />
              Choose Image File
            </GlassButton>
          </label>
        </Box>

        {selectedFile && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}>Preview:</Typography>
            <Avatar
              src={previewUrl}
              alt="Preview"
              sx={{ width: 100, height: 100, mx: 'auto' }}
            >
              <Castle />
            </Avatar>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
              {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
            </Typography>
          </Box>
        )}

        {uploadError && (
          <GlassInfoCard severity="error" sx={{ mt: 2 }}>
            {uploadError}
          </GlassInfoCard>
        )}
      </GlassDialog>
    </GlassBackground>
  );
};

export default NationManagement; 